import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ExternalLink, Trophy } from 'lucide-react'
import type { AscentItem, AttemptItem } from '../../services/training-diary'

type LocationKey = string

interface LocationGroup {
  key: LocationKey
  name: string | null
  ascents: AscentItem[]
  attempts: AttemptItem[]
}

function locationKey(item: { location_slug: string | null; location_type: string | null; location_name: string | null }): LocationKey {
  if (item.location_slug && item.location_type) {
    return `${item.location_type}:${item.location_slug}`
  }
  return `__name:${item.location_name ?? ''}`
}

function ascentClimbUrl(item: AscentItem): string | null {
  if (!item.location_slug || !item.location_type || !item.climb_type) return null
  return `/${item.location_type}s/${item.location_slug}/${item.climb_type}s/${item.climb_slug}`
}

function attemptClimbUrl(item: AttemptItem): string | null {
  if (!item.location_slug || !item.location_type || !item.climb_type) return null
  return `/${item.location_type}s/${item.location_slug}/${item.climb_type}s/${item.climb_slug}`
}

interface DayClimbsListProps {
  ascents: AscentItem[]
  attempts: AttemptItem[]
}

export function DayClimbsList({ ascents, attempts }: DayClimbsListProps) {
  const { t } = useTranslation()

  if (ascents.length === 0 && attempts.length === 0) return null

  const groups = new Map<LocationKey, LocationGroup>()
  const order: LocationKey[] = []

  function ensureGroup(item: { location_slug: string | null; location_type: string | null; location_name: string | null }) {
    const key = locationKey(item)
    let g = groups.get(key)
    if (!g) {
      g = { key, name: item.location_name, ascents: [], attempts: [] }
      groups.set(key, g)
      order.push(key)
    }
    return g
  }

  for (const a of ascents) ensureGroup(a).ascents.push(a)
  for (const a of attempts) ensureGroup(a).attempts.push(a)

  return (
    <div className="flex flex-col gap-2">
      {order.map((key) => {
        const g = groups.get(key)!
        return (
          <div key={key} className="flex flex-col gap-1">
            {g.name && (
              <div className="text-xs font-medium text-foreground/70">{g.name}</div>
            )}
            <div className="flex flex-col gap-1 pl-2">
              {g.ascents.map((item) => {
                const url = ascentClimbUrl(item)
                const sendLabel = item.send_type
                  ? t(`trainingDiary.ascentSendType.${item.send_type}`)
                  : null

                let countSuffix: string | null = null
                if (item.send_type === 'nd' && item.day_attempts_count > 0) {
                  countSuffix = t('trainingDiary.ascentCount.attempts', { count: item.day_attempts_count })
                } else if (item.send_type === 'worked') {
                  const total = item.attempts_count ?? item.day_attempts_count
                  countSuffix = t('trainingDiary.ascentCount.workedDetail', {
                    total,
                    today: item.day_attempts_count,
                  })
                }

                return (
                  <div key={`a-${item.climb_slug}`} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Trophy className="size-3 shrink-0 text-yellow-500" />
                    <span className="truncate flex-1 min-w-0">
                      <span className="font-medium text-foreground">{item.climb_name}</span>
                      {item.difficulty && (
                        <span className="ml-1 text-muted-foreground">· {item.difficulty}</span>
                      )}
                      {sendLabel && (
                        <span className="ml-1 text-muted-foreground">· {sendLabel}</span>
                      )}
                      {countSuffix && (
                        <span className="ml-1 text-muted-foreground">{countSuffix}</span>
                      )}
                    </span>
                    {url && (
                      <Link
                        to={url}
                        className="shrink-0 hover:text-foreground transition-colors"
                        title={item.climb_name}
                      >
                        <ExternalLink className="size-3" />
                      </Link>
                    )}
                  </div>
                )
              })}

              {g.attempts.map((item) => {
                const url = attemptClimbUrl(item)
                return (
                  <div key={`t-${item.climb_slug}`} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="shrink-0">🧗</span>
                    <span className="truncate flex-1 min-w-0">
                      <span className="font-medium text-foreground">{item.climb_name}</span>
                      {item.difficulty && (
                        <span className="ml-1 text-muted-foreground">· {item.difficulty}</span>
                      )}
                      {item.attempt_count > 1 && (
                        <span className="ml-1.5 text-muted-foreground">×{item.attempt_count}</span>
                      )}
                    </span>
                    {url && (
                      <Link
                        to={url}
                        className="shrink-0 hover:text-foreground transition-colors"
                        title={item.climb_name}
                      >
                        <ExternalLink className="size-3" />
                      </Link>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
